import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, LayoutGrid, List as ListIcon } from "lucide-react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { toast } from "sonner";

export default function AdminLeads() {
  const settings = useSettings();
  const stages = settings?.get("pipeline_stage") || [];
  const sources = settings?.get("lead_source") || [];
  const [view, setView] = useState("kanban");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const search = params.get("search") || "";

  const load = async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    try {
      const { data } = await api.get(`/leads?${q.toString()}`);
      setLeads(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of stages) g[s.key] = [];
    for (const l of leads) {
      if (!g[l.pipeline_stage]) g[l.pipeline_stage] = [];
      g[l.pipeline_stage].push(l);
    }
    return g;
  }, [stages, leads]);

  const onDragStart = (e, id) => e.dataTransfer.setData("text/plain", id);
  const onDrop = async (e, stageKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const original = leads.find((l) => l.id === id);
    if (!original || original.pipeline_stage === stageKey) return;
    setLeads((ls) => ls.map((l) => l.id === id ? { ...l, pipeline_stage: stageKey } : l));
    try {
      await api.post(`/leads/${id}/move`, { pipeline_stage: stageKey });
      toast.success(`Moved to ${stages.find((x) => x.key === stageKey)?.label}`);
    } catch {
      setLeads((ls) => ls.map((l) => l.id === id ? { ...l, pipeline_stage: original.pipeline_stage } : l));
      toast.error("Failed to move");
    }
  };

  return (
    <div data-testid="page-admin-leads">
      <AdminHeader breadcrumb="CRM · Investor Pipeline" title="Leads" subtitle="Manage your investor pipeline. Drag & drop across stages to update."
        actions={
          <>
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-altura-charcoal/50 absolute left-3 top-1/2 -translate-y-1/2" />
              <input placeholder="Search leads…" value={search}
                     onChange={(e) => setParams(e.target.value ? { search: e.target.value } : {})}
                     className="admin-input pl-9 w-64" data-testid="leads-search" />
            </div>
            <div className="flex rounded-md border border-altura-espresso/15 overflow-hidden">
              <button onClick={() => setView("kanban")} className={`px-3 py-2 text-xs ${view === "kanban" ? "bg-altura-espresso text-altura-ivory" : "text-altura-espresso"}`} data-testid="view-kanban"><LayoutGrid className="h-3.5 w-3.5" /></button>
              <button onClick={() => setView("list")} className={`px-3 py-2 text-xs ${view === "list" ? "bg-altura-espresso text-altura-ivory" : "text-altura-espresso"}`} data-testid="view-list"><ListIcon className="h-3.5 w-3.5" /></button>
            </div>
          </>
        }
      />
      <div className="p-6">
        {loading ? (
          <div className="text-sm text-altura-charcoal/60">Loading leads…</div>
        ) : view === "kanban" ? (
          <div className="flex gap-4 overflow-x-auto pb-4 scroll-elegant" data-testid="leads-kanban">
            {stages.map((s) => (
              <div key={s.key} className="kanban-col"
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={(e) => onDrop(e, s.key)}
                   data-testid={`kanban-col-${s.key}`}>
                <div className="flex items-center justify-between border-b border-altura-espresso/10 px-3 py-2.5">
                  <div className="text-xs font-medium uppercase tracking-[0.15em] text-altura-espresso">{s.label}</div>
                  <span className="font-mono text-[10px] text-altura-charcoal/50">{(grouped[s.key] || []).length}</span>
                </div>
                <div className="p-3 flex flex-col gap-2 min-h-[100px]">
                  {(grouped[s.key] || []).map((l) => (
                    <Link key={l.id} to={`/altura-command/leads/${l.id}`}
                          draggable onDragStart={(e) => onDragStart(e, l.id)}
                          className="kanban-card block" data-testid={`kanban-card-${l.id}`}>
                      <div className="font-editorial text-altura-espresso">{l.name}</div>
                      <div className="mt-1 text-[11px] text-altura-charcoal/60 truncate">{l.email || l.phone || "—"}</div>
                      <div className="mt-2 flex items-center gap-2 text-[10px] text-altura-charcoal/50">
                        {l.primary_market && <span className="badge-soft text-[9px]">{l.primary_market}</span>}
                        {l.source && <span className="text-altura-gold-dark">{(sources.find((x) => x.key === l.source)?.label) || l.source}</span>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="admin-card overflow-x-auto" data-testid="leads-list">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-[0.15em] text-altura-charcoal/50">
                <tr className="border-b border-altura-espresso/10">
                  <th className="text-left py-3 px-2">Name</th>
                  <th className="text-left py-3 px-2">Email</th>
                  <th className="text-left py-3 px-2">Market</th>
                  <th className="text-left py-3 px-2">Stage</th>
                  <th className="text-left py-3 px-2">Source</th>
                  <th className="text-left py-3 px-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-altura-espresso/5 hover:bg-altura-beige/30">
                    <td className="py-2.5 px-2"><Link className="text-altura-espresso font-medium hover:text-altura-gold-dark" to={`/altura-command/leads/${l.id}`} data-testid={`lead-row-${l.id}`}>{l.name}</Link></td>
                    <td className="py-2.5 px-2 text-altura-charcoal/75">{l.email || "—"}</td>
                    <td className="py-2.5 px-2">{l.primary_market || "—"}</td>
                    <td className="py-2.5 px-2"><span className="badge-soft">{stages.find((x) => x.key === l.pipeline_stage)?.label || l.pipeline_stage}</span></td>
                    <td className="py-2.5 px-2">{sources.find((x) => x.key === l.source)?.label || l.source}</td>
                    <td className="py-2.5 px-2 font-mono text-[11px] text-altura-charcoal/50">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan="6" className="py-10 text-center text-altura-charcoal/50">No leads yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
